// `arg` is passed straight to the tag API, e.g. `{ context: [...] }` for trackPageView.
export const SNOWPLOW_TRIGGER = (value: string, arg?: string | Record<string, unknown>) => {
  setTimeout(() => {
    if (arg) {
      return window?.snowplow?.(value, arg);
    }
    return window?.snowplow?.(value);
  }, 0);
};

interface SnowplowTracker {
  getDomainUserInfo: () => unknown;
}

interface SnowplowIds {
  snowplowDomainUserId?: string;
  snowplowNetworkUserId?: string;
  snowplowSessionId?: string;
}

interface SnowplowReadyIds extends SnowplowIds {
  snowplowReadiness: 'timeout' | 'tracker';
}

type SnowplowTrackerCallback = (this: unknown) => void;

// Maximum delay direct sign-in accepts while waiting for the asynchronously loaded tracker.
// Keep this bounded so unavailable analytics can never block authentication indefinitely.
export const DEFAULT_SNOWPLOW_TRACKER_TIMEOUT_MS = 250;

// XXX: https://github.com/snowplow/snowplow-javascript-tracker/blob/8fa9bc58280860e5d59dedffc1bb130ca74af28c/trackers/javascript-tracker/src/in_queue.ts#L260
export const SNOWPLOW_GET_TRACKERS = (callback: SnowplowTrackerCallback) => window?.snowplow?.(callback);

const getNetworkUserId = () => document.cookie.split('; ').find((cookie) => cookie.startsWith('sp='))?.split('=')[1];

const isSnowplowTracker = (value: unknown): value is SnowplowTracker => typeof value === 'object'
  && value !== null
  && 'getDomainUserInfo' in value
  && typeof value.getDomainUserInfo === 'function';

const normalizeId = (value: unknown) => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = value.trim();
  return normalizedValue || undefined;
};

const getTrackerIds = (trackers: unknown): Pick<SnowplowIds, 'snowplowDomainUserId' | 'snowplowSessionId'> => {
  if (typeof trackers !== 'object' || trackers === null) {
    return {};
  }

  const firstTracker = Object.values(trackers)[0];
  if (!isSnowplowTracker(firstTracker)) {
    return {};
  }

  const domainUserInfo = firstTracker.getDomainUserInfo();
  if (!Array.isArray(domainUserInfo)) {
    return {};
  }

  return {
    snowplowDomainUserId: normalizeId(domainUserInfo[1]),
    snowplowSessionId: normalizeId(domainUserInfo[6]),
  };
};

const getIds = (trackers?: unknown): SnowplowIds => ({
  ...getTrackerIds(trackers),
  snowplowNetworkUserId: normalizeId(getNetworkUserId()),
});

export const getSnowplowIds = () => {
  let ids = getIds();

  SNOWPLOW_GET_TRACKERS(function getTrackers() {
    ids = getIds(this);
  });

  return ids;
};

export const getSnowplowIdsWhenReady = (
  timeoutMs = DEFAULT_SNOWPLOW_TRACKER_TIMEOUT_MS,
): Promise<SnowplowReadyIds> => new Promise((resolve) => {
  let settled = false;
  let timeout: ReturnType<typeof setTimeout>;

  const finish = (ids: SnowplowIds, snowplowReadiness: SnowplowReadyIds['snowplowReadiness']) => {
    if (!settled) {
      settled = true;
      clearTimeout(timeout);
      resolve({ ...ids, snowplowReadiness });
    }
  };

  timeout = setTimeout(() => finish(getIds(), 'timeout'), timeoutMs);

  SNOWPLOW_GET_TRACKERS(function getTrackers() {
    finish(getIds(this), 'tracker');
  });
});
